import { computed, type Ref } from 'vue'
import { WorkflowStatus, QuotationStep, TyreOrderStep, VehicleOrderStep, type WorkflowDto } from '@/api/eOrdersApi'
import { PurchaseOrder, isVehicleOrderType } from '@/types/enums/PurchaseOrder.ts'
import { Permission } from '@/types/enums/Permission.ts'
import { useUser } from '@/store'

const finalStatuses: WorkflowStatus[] = [
  WorkflowStatus.Completed,
  WorkflowStatus.Archived,
  WorkflowStatus.Rejected,
  WorkflowStatus.QuotationRejection,
  WorkflowStatus.OrderRejection,
  WorkflowStatus.DeliveryRejection
];

const finalTyreOrderSteps: TyreOrderStep[] = [TyreOrderStep.Delivered, TyreOrderStep.Rejection, TyreOrderStep.Rejected];
const finalVehicleOrderSteps: VehicleOrderStep[] = [VehicleOrderStep.Completed, VehicleOrderStep.Rejected];

const assignMeQuotationPermissions: Permission[] = [
  Permission.ClientAccount
];

const assignMeOrderPermissions: Permission[] = [
  Permission.DeliverySpecialist,
  Permission.DeliveryManager
];

export function useAssignMe(workflow: Ref<WorkflowDto | null>, selectedPurchaseOrder: Ref<any>) {
  const userStore = useUser();
  
  const relevantAssignees = computed(() => {
    const status = workflow.value?.status;
    
    if (status === WorkflowStatus.QuotationApproval) return workflow.value?.quotation?.assignees ?? [];
    if (status === WorkflowStatus.Delivery) return workflow.value?.delivery?.assignees ?? [];
    
    return selectedPurchaseOrder.value?.assignees ?? [];
  });
  
  const isPersonAssignedToMe = computed(() => {
    const me = userStore.user.id;
    
    if (!me) return false;
    
    return relevantAssignees.value.some((a: any) => a.keycloakId === me || a.user?.keycloakId === me);
  });
  
  const isRoleAssignedToMe = computed(() => {
    const assigneeRoles = relevantAssignees.value.map((a: any) => a.role).filter((role: string | undefined): role is string => !!role);
    const userRoles = userStore.getUserRoles();
    
    return assigneeRoles.some(assigneeRole =>
      userRoles.some(userRole => userRole === assigneeRole || userRole === `role.${assigneeRole}`)
    );
  });
  
  const isAssignedToMe = computed(() => isPersonAssignedToMe.value || isRoleAssignedToMe.value);
  
  const canAssignMe = computed(() => {
    if (!workflow.value) return false;
    if (finalStatuses.includes(workflow.value.status)) return false;
    if (isAssignedToMe.value) return false;
    
    const status = workflow.value.status;
    
    if (status === WorkflowStatus.QuotationApproval) {
      if (!assignMeQuotationPermissions.some(p => userStore.hasPermission(p))) return false;
      
      if (workflow.value.quotation?.currentProcessStep === QuotationStep.Parked) return false;
      
      return true;
    }
    
    if ([WorkflowStatus.OrderApproval, WorkflowStatus.OrderProcessing, WorkflowStatus.OrderUpdated, WorkflowStatus.Delivery].includes(status)) {
      if (!assignMeOrderPermissions.some(p => userStore.hasPermission(p))) return false;
      
      const po = selectedPurchaseOrder.value;
      
      if (po) {
        if (po.type === PurchaseOrder.Tyre && finalTyreOrderSteps.includes(po.currentProcessStep as TyreOrderStep)) return false;
        
        if (isVehicleOrderType(po.type) && finalVehicleOrderSteps.includes(po.currentProcessStep as VehicleOrderStep)) return false;
        
        if (po.currentProcessStep === TyreOrderStep.Parked || po.currentProcessStep === VehicleOrderStep.Parked) return false;
      }
      
      return true;
    }
    
    return false;
  });
  
  return { isAssignedToMe, canAssignMe };
}
